import { Center, Loader, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { booksApi } from "@/api/books";
import { bookKeys } from "@/api/queryKeys";
import { ReaderRouter } from "@/components/reader";
import { useDynamicDocumentTitle } from "@/hooks/useDocumentTitle";
import { useViewportZoomLock } from "@/hooks/useViewportZoomLock";

/**
 * Reader page component.
 *
 * Loads book data and passes it to the appropriate reader component.
 * Handles navigation back to book detail page on close.
 */
export function Reader() {
  const { bookId } = useParams<{ bookId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract page query parameter (e.g., ?page=17) for comics/PDFs
  const pageParam = searchParams.get("page");
  const startPage = pageParam ? Number.parseInt(pageParam, 10) : undefined;

  // Extract incognito parameter (e.g., ?incognito=true) for reading without progress tracking
  const incognito = searchParams.get("incognito") === "true";

  // Fetch book details (includes effective reading direction from series/library)
  const {
    data: bookDetail,
    isLoading: bookLoading,
    error: bookError,
  } = useQuery({
    queryKey: bookKeys.detail(bookId),
    queryFn: () => booksApi.getDetail(bookId as string),
    enabled: !!bookId,
  });

  // Set document title to book name for browser history
  useDynamicDocumentTitle(bookDetail?.book?.title, "Reading");

  // Disable native viewport zoom while reading: it scales the whole UI (not just
  // the page) and gets stuck on iOS. Content-only zoom is handled in the reader.
  useViewportZoomLock();

  const handleClose = () => {
    // Navigate back to book detail page
    if (bookId) {
      navigate(`/books/${bookId}`);
    } else {
      navigate(-1);
    }
  };

  // Loading state
  if (bookLoading) {
    return (
      <Center
        style={{ width: "100vw", height: "100dvh", backgroundColor: "#000" }}
      >
        <Loader size="lg" color="gray" />
      </Center>
    );
  }

  // Error state
  if (bookError || !bookDetail) {
    return (
      <Center
        style={{ width: "100vw", height: "100dvh", backgroundColor: "#000" }}
      >
        <Text c="red">
          {bookError instanceof Error
            ? bookError.message
            : "Failed to load book"}
        </Text>
      </Center>
    );
  }

  const { book } = bookDetail;

  return (
    <ReaderRouter
      bookId={book.id}
      seriesId={book.seriesId}
      title={book.title}
      totalPages={book.pageCount}
      format={book.fileFormat}
      fileSize={book.fileSize}
      readingDirection={book.readingDirection ?? null}
      analyzed={book.analyzed}
      startPage={startPage}
      incognito={incognito}
      onClose={handleClose}
    />
  );
}
